import { Directive, ElementRef, forwardRef, HostBinding, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';

import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yBooleanInput } from '../interfaces/a11y.interfaces';
import { A11yKeyboardService } from '../services/a11y-keyboard.service';
import {
  normalizeAriaValue,
  toBoolean,
  toRequiredAriaBoolean
} from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';
import { A11yRadioGroupDirective } from './a11y-radio-group.directive';

@Directive({
  selector: '[khA11yRadio]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yRadioDirective) }]
})
export class A11yRadioDirective extends A11yLabelDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly radioGroup = inject(A11yRadioGroupDirective, { optional: true, skipSelf: true });
  private readonly keyboardService = inject(A11yKeyboardService);

  @Input('khA11yRadio') public checked: boolean | string | null = false;
  @Input() public khA11yRadioTabindex: string | number = 0;
  @Input() public disabled: A11yBooleanInput = false;

  @HostBinding('attr.role') public readonly role = 'radio';

  @HostBinding('attr.aria-checked')
  public get ariaChecked(): 'true' | 'false' {
    return toRequiredAriaBoolean(this.checked);
  }

  @HostBinding('attr.tabindex')
  public get tabindex(): string {
    return this.isDisabled ? '-1' : normalizeAriaValue(`${this.khA11yRadioTabindex}`) ?? '0';
  }

  @HostBinding('attr.aria-disabled')
  public get ariaDisabled(): string | null {
    return this.isDisabled ? 'true' : null;
  }

  public get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  public get isDisabled(): boolean {
    return toBoolean(this.disabled, true);
  }

  public ngOnInit(): void {
    this.radioGroup?.registerRadio(this);
  }

  public ngOnDestroy(): void {
    this.radioGroup?.unregisterRadio(this);
  }

  public activate(): void {
    this.element.click();
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (this.isDisabled) {
      return;
    }

    if (this.radioGroup?.handleRadioKeydown(this, event)) {
      return;
    }

    this.keyboardService.activateOnEnterOrSpace(event, () => {
      this.activate();
    });
  }
}
